import { useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { fetchMyAuditEvents } from '@/api/audit';
import { Button } from '@/components/ui/Button';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { Spinner } from '@/components/ui/Spinner';
import { useI18n, type MessageKey } from '@/i18n';
import type { AuditEvent } from '@/types';

const EVENT_KEYS: Record<string, MessageKey> = {
  login: 'event.login',
  logout: 'event.logout',
  'library.view': 'event.libraryView',
  'game.view': 'event.gameView',
  'demo.launch': 'event.demoLaunch',
  'demo.launch_failed': 'event.demoLaunchFailed',
  'asset.download': 'event.assetDownload',
  'guideline.view': 'event.guidelineView',
  'roadmap.view': 'event.roadmapView',
};

export function AccountPage() {
  const { email, signOut, accessToken, sessionId } = useAuth();
  const { t, locale } = useI18n();

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activityUnavailable, setActivityUnavailable] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    setLoading(true);
    fetchMyAuditEvents(accessToken, 20, sessionId)
      .then(setEvents)
      .catch(() => setActivityUnavailable(true))
      .finally(() => setLoading(false));
  }, [accessToken, sessionId]);

  return (
    <section className="account-page site-pad-x page-narrow">
      <header className="page-header reveal is-in">
        <p className="eyebrow">{t('account.eyebrow')}</p>
        <h1 className="page-title display">{t('account.title')}</h1>
      </header>

      <div className="account-card glass-panel-soft reveal is-in reveal-delay-1">
        <div className="account-card__row">
          <span className="account-card__label">{t('account.email')}</span>
          <span className="account-card__value">{email ?? '—'}</span>
        </div>
        <div className="account-card__row">
          <span className="account-card__label">{t('account.language')}</span>
          <LanguageSelector align="start" />
        </div>
        <Button variant="glass" onClick={() => signOut()}>
          {t('nav.signOut')}
        </Button>
      </div>

      <section className="account-activity reveal is-in reveal-delay-2">
        <h2 className="section-title">{t('account.recentActivity')}</h2>
        {loading ? (
          <Spinner label={t('account.loadingActivity')} />
        ) : activityUnavailable ? (
          <p className="muted-text">{t('account.activityUnavailable')}</p>
        ) : events.length === 0 ? (
          <p className="muted-text">{t('account.noActivity')}</p>
        ) : (
          <ul className="activity-list">
            {events.map((event, index) => (
              <li key={event.id ?? index} className="activity-item glass-panel-soft">
                <span className="activity-item__type">
                  {EVENT_KEYS[event.type] ? t(EVENT_KEYS[event.type]) : event.type}
                </span>
                {event.resource ? (
                  <span className="activity-item__resource">{event.resource}</span>
                ) : null}
                {event.createdAt ? (
                  <time className="activity-item__time" dateTime={event.createdAt}>
                    {new Date(event.createdAt).toLocaleString(locale)}
                  </time>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
